import { menusRenderer } from 'configs/menu';
import { Icon } from 'components/icon';
import React, { useState, useEffect } from 'react';

import MenuItems from './MenuItem';
import { MainRouteKeys } from 'routes/enums';
import { Roles } from 'configs/roles';
import { useGetUserProfileQuery, useGetAdminActionsQuery } from 'services';

const Navigator = (): JSX.Element => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false); // For Desktop
  const { data: profile, isError: isProfileError } = useGetUserProfileQuery();
  const { data: permissions, isError: isPermissionsError } =
    useGetAdminActionsQuery();

  // Query updates must drive the menu; storage writes do not trigger React renders.
  const menus =
    profile && !isProfileError
      ? menusRenderer({
          roles: profile.role ? [profile.role as Roles] : [],
          adminActions: isPermissionsError
            ? []
            : permissions?.adminActions || [],
        })
      : [];

  const openSidebar = () => setIsSidebarOpen(true);
  const closeSidebar = () => setIsSidebarOpen(false);
  const toggleMobileSidebar = () => setIsSidebarOpen((v) => !v);
  const toggleDesktopSidebar = () => setIsCollapsed((v) => !v);

  // Close sidebar when window resizes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  return (
    <>
      {/* Mobile backdrop overlay */}
      <div
        className={`sidebar-backdrop ${isSidebarOpen ? 'active' : ''}`}
        onClick={closeSidebar}
        aria-hidden='true'
      />

      {/* Mobile hamburger — outside sidebar, always visible on mobile */}
      <button
        className='toggle-button_open'
        type='button'
        onClick={openSidebar}
        aria-label='Mở menu'
        style={{ display: isSidebarOpen ? 'none' : undefined }}
      >
        <Icon.Menu />
      </button>

      {/* Sidebar */}
      <div
        className={`Navigator ${isSidebarOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}
      >
        <div className='menu'>
          {/* Sidebar Header: Logo and PC toggle button */}
          <div className='sidebar-header'>
            <a className='logo' href={MainRouteKeys.PersonalInfo}>
              <img
                src='/assets/images/logo-symbol-white.png'
                alt='Logo'
                className='logo-img'
              />
            </a>
            <button
              className='toggle-desktop-btn'
              onClick={toggleDesktopSidebar}
              title={isCollapsed ? 'Mở rộng sidebar' : 'Thu gọn sidebar'}
            >
              <Icon.Menu />
            </button>
          </div>

          {/* Mobile close button (inside sidebar) */}
          <button
            className='toggle-button'
            type='button'
            onClick={closeSidebar}
            aria-label='Đóng menu'
          >
            <Icon.Close />
          </button>

          {/* Menu items */}
          {menus.map((menu) => (
            <MenuItems
              data={menu}
              key={menu.path}
              onClickCallback={closeSidebar}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default Navigator;
